const DEFAULTS = {
  pAmount: 200,
  pSize: [0.5, 2.5],
  pSwing: [0.1, 1],
  pSpeed: [40, 60],
  pAmplitude: [25, 50],
};

const MAX_DELTA_TIME = 1 / 30;

function Vector2(x, y) {
  this.x = x || 0;
  this.y = y || 0;

  this.add = function add(other) {
    this.x += other.x;
    this.y += other.y;
  };

  this.magnitude = function magnitude() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  };
}

function frand(min, max) {
  return (min || 0) + Math.random() * ((max || 1) - (min || 0));
}

function microtime() {
  return new Date().getTime() * 0.001;
}

function parseNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseRange(value, fallback) {
  if (!value) return fallback;

  const parts = value.split(',').map((part) => Number(part.trim()));

  if (parts.length === 2 && parts.every((part) => Number.isFinite(part))) {
    return parts;
  }

  return fallback;
}

function parseBoolean(value, fallback = false) {
  if (value === undefined) return fallback;

  return value !== 'false' && value !== '0';
}

function Particle(origin, velocity, diameter, amplitude) {
  this.origin = origin;
  this.position = new Vector2(origin.x, origin.y);
  this.velocity = velocity || new Vector2(0, 0);
  this.radius = diameter / 2;
  this.amplitude = amplitude;
  this.dx = Math.random() * 100;

  this.update = function update(deltaTime) {
    this.position.y += this.velocity.y * deltaTime;
    this.dx += this.velocity.x * deltaTime;
    this.position.x = this.origin.x + this.amplitude * Math.sin(this.dx);
  };
}

class Snow {
  static prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  constructor(element) {
    if (!(element instanceof HTMLElement)) {
      throw new TypeError('Invalid element');
    }

    this.container = element;
    this.config = Snow.parseConfig(element.dataset);
    this.reducedMotion = Snow.prefersReducedMotion();
    this.particles = [];
    this.running = false;
    this.frameTime = 0;
    this.resizeObserver = null;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'snow__canvas';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    this.resize = this.resize.bind(this);
    this.loop = this.loop.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.resize();
    this.initParticles();
    this.observeResize();
    this.start();
  }

  static parseConfig(dataset) {
    return {
      pAmount: parseNumber(dataset.pAmount, DEFAULTS.pAmount),
      pSize: parseRange(dataset.pSize, DEFAULTS.pSize),
      pSwing: parseRange(dataset.pSwing, DEFAULTS.pSwing),
      pSpeed: parseRange(dataset.pSpeed, DEFAULTS.pSpeed),
      pAmplitude: parseRange(dataset.pAmplitude, DEFAULTS.pAmplitude),
      prefill: parseBoolean(dataset.pPrefill, false),
    };
  }

  observeResize() {
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(this.container);
      return;
    }

    window.addEventListener('resize', this.resize, { passive: true });
  }

  start() {
    this.running = true;
    this.frameTime = microtime();
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.reducedMotion) {
      this.draw();
      return;
    }

    this.loop();
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.frameTime = microtime();
    }
  }

  stop() {
    this.running = false;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener('resize', this.resize);
    }
  }

  resize() {
    const { width, height } = this.container.getBoundingClientRect();
    this.canvas.width = width;
    this.canvas.height = height;
    this.initParticles();

    if (this.reducedMotion) {
      this.draw();
    }
  }

  initParticles() {
    const {
      pAmount, pSize, pSwing, pSpeed, pAmplitude, prefill,
    } = this.config;

    this.particles.length = 0;

    const usePrefill = prefill || this.reducedMotion;
    const yMin = usePrefill ? 0 : -this.canvas.height;
    const yMax = usePrefill ? this.canvas.height : 0;

    for (let i = 0; i < pAmount; i += 1) {
      const diameter = frand(pSize[0], pSize[1]);
      const origin = new Vector2(frand(0, this.canvas.width), frand(yMin, yMax));
      const velocity = new Vector2(frand(pSwing[0], pSwing[1]), frand(pSpeed[0], pSpeed[1]));
      const amplitude = frand(pAmplitude[0], pAmplitude[1]);

      this.particles.push(new Particle(origin, velocity, diameter, amplitude));
    }
  }

  loop() {
    if (!this.running) return;

    this.clear();
    this.update();
    this.draw();
    window.requestAnimationFrame(this.loop);
  }

  update() {
    const now = microtime();
    const deltaTime = Math.min(now - this.frameTime, MAX_DELTA_TIME);

    for (let i = 0; i < this.particles.length; i += 1) {
      const particle = this.particles[i];
      particle.update(deltaTime);

      if (particle.position.y - particle.radius > this.canvas.height) {
        particle.position.y = -particle.radius;
        const newX = Math.random() * this.canvas.width;
        particle.position.x = newX;
        particle.origin.x = newX;
        particle.dx = Math.random() * 100;
      }
    }

    this.frameTime = now;
  }

  draw() {
    const { ctx } = this;

    ctx.fillStyle = 'rgb(255, 255, 255)';

    for (let i = 0; i < this.particles.length; i += 1) {
      const particle = this.particles[i];
      const { x, y } = particle.position;

      ctx.beginPath();
      ctx.arc(x, y, particle.radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}

export default Snow;
